import { useMemo } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { Plus, FolderKanban, ExternalLink } from 'lucide-react';
import { useProjects } from '@/hooks';
import { DataTable } from '@/components/DataTable';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  EmptyState,
  SkeletonTable,
} from '@/components/ui';
import { formatRelativeTime, capitalize } from '@/lib/utils';
import type { Project, ProjectStatus } from '@/types';

const columnHelper = createColumnHelper<Project>();

export function ProjectsList() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useProjects();

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: (info) => (
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-accent/10">
              <FolderKanban className="h-4 w-4 text-accent" />
            </div>
            <div>
              <p className="font-medium text-foreground">{info.getValue()}</p>
              <p className="text-xs text-foreground-subtle truncate max-w-[200px]">
                {info.row.original.description}
              </p>
            </div>
          </div>
        ),
      }),
      columnHelper.accessor('repository', {
        header: 'Repository',
        cell: (info) => (
          <a
            href={info.getValue()}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-accent hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="truncate max-w-[180px]">
              {info.getValue().replace('https://github.com/', '')}
            </span>
            <ExternalLink className="h-3 w-3" />
          </a>
        ),
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: (info) => {
          const status = info.getValue();
          const variantMap: Record<ProjectStatus, 'success' | 'warning' | 'default'> = {
            active: 'success',
            paused: 'warning',
            archived: 'default',
          };
          return (
            <Badge variant={variantMap[status]}>
              {capitalize(status)}
            </Badge>
          );
        },
      }),
      columnHelper.accessor('stats.totalWorkflows', {
        header: 'Workflows',
        cell: (info) => (
          <span className="text-foreground-muted">{info.getValue()}</span>
        ),
      }),
      columnHelper.accessor('stats.successfulRuns', {
        header: 'Success Rate',
        cell: (info) => {
          const stats = info.row.original.stats;
          const total = stats.successfulRuns + stats.failedRuns;
          if (total === 0) return <span className="text-foreground-muted">-</span>;
          const rate = Math.round((stats.successfulRuns / total) * 100);
          return (
            <span
              className={
                rate >= 90
                  ? 'text-success'
                  : rate >= 70
                  ? 'text-warning'
                  : 'text-error'
              }
            >
              {rate}%
            </span>
          );
        },
      }),
      columnHelper.accessor('updatedAt', {
        header: 'Last Updated',
        cell: (info) => (
          <span className="text-foreground-muted">
            {formatRelativeTime(info.getValue())}
          </span>
        ),
      }),
    ],
    []
  );

  const projects = data?.data ?? [];

  const handleRowClick = (project: Project) => {
    navigate({ to: '/projects/$id', params: { id: project.id } });
  };

  if (error) {
    return (
      <Card variant="bordered">
        <CardContent className="py-12">
          <EmptyState
            icon={<FolderKanban className="h-12 w-12" />}
            title="Failed to load projects"
            description="An error occurred while loading the projects. Please try again."
            action={
              <Button variant="secondary" onClick={() => window.location.reload()}>
                Retry
              </Button>
            }
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Projects</h1>
          <p className="text-foreground-muted mt-1">
            Manage your development projects
          </p>
        </div>
        <Button>
          <Plus className="h-4 w-4" />
          New Project
        </Button>
      </div>

      {/* Projects table */}
      <Card variant="bordered">
        <CardHeader>
          <CardTitle>All Projects</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <SkeletonTable rows={5} />
          ) : projects.length === 0 ? (
            <EmptyState
              icon={<FolderKanban className="h-12 w-12" />}
              title="No projects yet"
              description="Create your first project to get started with autonomous development."
              action={
                <Button>
                  <Plus className="h-4 w-4" />
                  Create Project
                </Button>
              }
            />
          ) : (
            <DataTable
              columns={columns}
              data={projects}
              onRowClick={handleRowClick}
              pageSize={10}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
